import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const MAX_DEVICES = 3;
const DEVICE_ID_KEY = "ayeris_device_id";

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown Device";
}

export const useDeviceLimit = () => {
  const { user, signOut } = useAuth();
  const checkedRef = useRef(false);

  const registerDevice = useCallback(async () => {
    if (!user) return { allowed: true };

    const deviceId = getOrCreateDeviceId();
    const deviceName = getDeviceName();

    // Upsert this device
    await (supabase.from("user_devices") as any).upsert({
      user_id: user.id,
      device_id: deviceId,
      device_name: deviceName,
      last_active: new Date().toISOString(),
    }, { onConflict: "user_id,device_id" });

    // Check total devices
    const { data: devices } = await (supabase
      .from("user_devices") as any)
      .select("*")
      .eq("user_id", user.id)
      .order("last_active", { ascending: true });

    if (devices && devices.length > MAX_DEVICES) {
      // Check if current device is among the most recent MAX_DEVICES
      const allowed = devices.slice(-MAX_DEVICES);
      const isAllowed = allowed.some((d: any) => d.device_id === deviceId);

      if (!isAllowed) {
        return { allowed: false, deviceCount: devices.length };
      }

      // Remove oldest devices beyond the limit
      const toRemove = devices.slice(0, devices.length - MAX_DEVICES);
      for (const d of toRemove) {
        await (supabase.from("user_devices") as any).delete().eq("id", d.id);
      }
    }

    return { allowed: true };
  }, [user]);

  // Auto-register on login
  useEffect(() => {
    if (user && !checkedRef.current) {
      checkedRef.current = true;
      registerDevice().then(({ allowed }) => {
        if (!allowed) {
          alert("Too many devices. You've been signed out. Please remove a device from another session first.");
          signOut();
        }
      });
    }
    if (!user) {
      checkedRef.current = false;
    }
  }, [user, registerDevice, signOut]);

  // Heartbeat every 5 minutes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      const deviceId = getOrCreateDeviceId();
      (supabase.from("user_devices") as any).update({
        last_active: new Date().toISOString(),
      }).eq("user_id", user.id).eq("device_id", deviceId).then(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  return { registerDevice };
};
